import { z } from 'zod';
import { ASPECT_RATIOS, BOARD_KINDS, BOARD_NODE_TYPES, FILE_KINDS, SHARE_TARGETS } from '../domain';
import { patchOf } from './patch';
import { reviewAnchor } from './tasks';
import { richTextSchema } from './rich-text';

export const shareLinkInput = z.object({
  targetType: z.enum(SHARE_TARGETS),
  targetId: z.uuid(),
  title: z
    .string()
    .trim()
    .max(160)
    .optional()
    .transform((v) => v ?? ''),
  /** Vazio = sem senha. */
  password: z.string().min(4, 'Senha com pelo menos 4 caracteres').max(128).nullable().optional(),
  expiresAt: z.iso.datetime({ offset: true }).nullable().optional(),
  allowDownload: z.boolean().default(false),
  allowApproval: z.boolean().default(false),
  allowComments: z.boolean().default(false),
});
export type ShareLinkInput = z.input<typeof shareLinkInput>;
export const shareLinkPatch = patchOf(shareLinkInput.omit({ targetType: true, targetId: true }));

export const shareLinkDto = z.object({
  id: z.uuid(),
  targetType: z.enum(SHARE_TARGETS),
  targetId: z.uuid(),
  title: z.string(),
  url: z.string(),
  hasPassword: z.boolean(),
  expiresAt: z.string().nullable(),
  allowDownload: z.boolean(),
  allowApproval: z.boolean(),
  allowComments: z.boolean(),
  revokedAt: z.string().nullable(),
  viewCount: z.number(),
  lastViewedAt: z.string().nullable(),
  createdBy: z.string().nullable(),
  createdAt: z.string(),
});
export type ShareLinkDto = z.infer<typeof shareLinkDto>;

// Página pública: só o que o cliente pode ver (SPEC §19). ----------------------------

export const publicFileDto = z.object({
  id: z.uuid(),
  name: z.string(),
  kind: z.enum(FILE_KINDS),
  mimeType: z.string(),
  sizeBytes: z.number(),
  url: z.string(),
  thumbnailUrl: z.string().nullable(),
});
export type PublicFileDto = z.infer<typeof publicFileDto>;

const publicNode = z.object({
  id: z.uuid(),
  type: z.enum(BOARD_NODE_TYPES),
  x: z.number(),
  y: z.number(),
  width: z.number().nullable(),
  height: z.number().nullable(),
  parentId: z.uuid().nullable(),
  fileId: z.uuid().nullable(),
  data: z.record(z.string(), z.unknown()),
});

const publicScene = z.object({
  id: z.uuid(),
  title: z.string(),
  groupLabel: z.string(),
  durationMs: z.number(),
  frameFileId: z.uuid().nullable(),
  videoFileId: z.uuid().nullable(),
  description: z.string(),
  action: z.string(),
  motion: z.string(),
  transition: z.string(),
  audio: z.string(),
  onScreenText: z.string(),
});
export type PublicSceneDto = z.infer<typeof publicScene>;

export const publicShareDto = z.object({
  locked: z.boolean(),
  title: z.string(),
  org: z.object({ name: z.string(), logoUrl: z.string().nullable() }),
  kind: z.enum(SHARE_TARGETS).nullable(),
  allowDownload: z.boolean(),
  allowApproval: z.boolean(),
  allowComments: z.boolean(),
  /** Comentários visíveis ao cliente nos arquivos do link (quando o link permite comentar). */
  comments: z.array(
    z.object({
      id: z.uuid(),
      fileId: z.uuid(),
      authorName: z.string(),
      text: z.string(),
      anchor: z.record(z.string(), z.unknown()).nullable(),
      pinNumber: z.number().nullable(),
      status: z.enum(['open', 'resolved']),
      createdAt: z.string(),
    }),
  ),
  /** Aprovações pendentes dos arquivos mostrados (só quando o link permite aprovar). */
  approvals: z.array(
    z.object({
      id: z.uuid(),
      fileId: z.uuid(),
      fileName: z.string(),
      versionNumber: z.number(),
      message: z.string().nullable(),
    }),
  ),
  /** Chave de acesso depois da senha (vai nas URLs dos arquivos). */
  key: z.string().nullable(),
  board: z
    .object({
      kind: z.enum(BOARD_KINDS),
      title: z.string(),
      description: z.string().nullable(),
      context: z.string().nullable(),
      nodes: z.array(publicNode),
    })
    .nullable(),
  storyboard: z
    .object({
      title: z.string(),
      description: z.string().nullable(),
      context: z.string().nullable(),
      aspectRatio: z.enum(ASPECT_RATIOS),
      versionNumber: z.number(),
      scenes: z.array(publicScene),
    })
    .nullable(),
  project: z
    .object({
      name: z.string(),
      clientName: z.string().nullable(),
      description: richTextSchema.nullable(),
      coverFileId: z.uuid().nullable(),
      startDate: z.string().nullable(),
      dueDate: z.string().nullable(),
      fileIds: z.array(z.uuid()),
    })
    .nullable(),
  fileId: z.uuid().nullable(),
  files: z.array(publicFileDto),
});
export type PublicShareDto = z.infer<typeof publicShareDto>;

export const publicCommentInput = z.object({
  fileId: z.uuid(),
  text: z.string().trim().min(1, 'Escreva o comentário').max(4000),
  name: z.string().trim().min(2, 'Seu nome').max(120),
  anchor: reviewAnchor.nullable().optional(),
});
